/**
 * ProducersListPage — /feed/producers (US-048-08)
 *
 * One row per system feed producer: status, kill switch, last run and 7-day
 * counts. The kill switch never flips optimistically: the toggle shows a
 * spinner while the PUT runs, then the list is re-fetched and the server's
 * value is rendered (a false "off" is worse than a slow one).
 *
 * Journals.API cold start can take ~90 s, so every call uses
 * COLDSTART_RETRY_CONFIG (via the service). Never shorten those timeouts.
 */

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import {
  getProducers,
  setGlobalDailyCap,
  setProducerEnabled,
  ProducersHttpError,
  type ProducerListItem,
  type ProducerListResponse,
  type SkippedByReason,
} from '../../services/adminProducersService';
import {
  PRODUCER_STATUS_CLASS,
  PRODUCER_STATUS_LABEL,
} from '../../utils/producerStatus';

const DESCRIPTIONS: Record<string, string> = {
  BoardMilestone: 'Celebrates when someone crosses a board milestone, shared anonymously with peers.',
  Comeback: 'Writes a private welcome-back note when someone returns after a quiet spell.',
  CohortPulse: 'Weekly aggregate of what people who joined in the same month got done.',
  CategoryHeartbeat: 'Weekly aggregate of progress across boards in the same category.',
  WeekInReview: 'Weekly personal summary of what the user finished.',
  QuestionOfDay: 'A daily reflection question posted to the feed.',
  WinDetected: 'Highlights a win when a meaningful task is completed.',
  CheckInInvite: 'Invites the user to check in when a board has gone quiet.',
  AgentProgress: 'Reports progress made by the proactive agent on the user\'s behalf.',
};

const FALLBACK_DESCRIPTION = 'System feed producer.';

/** Kill-switch-only producers (d22): no tuning fields and no run metrics here. */
const KILL_SWITCH_ONLY = new Set([
  'WeekInReview',
  'QuestionOfDay',
  'WinDetected',
  'CheckInInvite',
  'AgentProgress',
]);

const SKIP_REASON_LABELS: ReadonlyArray<[keyof SkippedByReason, string]> = [
  ['privacyFloor', 'Privacy floor'],
  ['consentOff', 'Consent off'],
  ['dailyCap', 'Daily cap'],
  ['duplicate', 'Duplicate'],
  ['notSubstantive', 'Not substantive'],
  ['disabled', 'Disabled'],
];

const NEVER_RUN_COPY =
  'No runs recorded yet. This producer uses the default settings from appsettings.';

function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'Never';
  const diffMs = now - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return 'Unknown';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function DefaultBadge() {
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-zinc-800 text-zinc-400 border border-zinc-700">
      default
    </span>
  );
}

function StatusPill({ status }: { status: ProducerListItem['status'] }) {
  return (
    <span
      data-testid="status-pill"
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${PRODUCER_STATUS_CLASS[status]}`}
    >
      {PRODUCER_STATUS_LABEL[status]}
    </span>
  );
}

interface KillSwitchProps {
  producer: string;
  enabled: boolean;
  pending: boolean;
  onToggle: () => void;
}

function KillSwitch({ producer, enabled, pending, onToggle }: KillSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={`${producer} enabled`}
      aria-busy={pending}
      disabled={pending}
      onClick={onToggle}
      className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-70 ${
        enabled ? 'bg-violet-600' : 'bg-zinc-700'
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-zinc-700 transition-transform ${
          enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      >
        {pending && (
          <Loader2
            role="status"
            aria-label={`Saving ${producer}`}
            className="w-3 h-3 motion-safe:animate-spin"
          />
        )}
      </span>
    </button>
  );
}

function SkipBreakdown({ item }: { item: ProducerListItem }) {
  const lines = SKIP_REASON_LABELS.map(
    ([key, label]) => `${label}: ${item.skippedByReason7d[key]}`,
  );
  return (
    <span className="relative group inline-block">
      <span
        tabIndex={0}
        title={lines.join('\n')}
        aria-label={`Skipped (7d) ${item.skipped7d}. ${lines.join(', ')}`}
        className="underline decoration-dotted cursor-help focus:outline-none focus:ring-2 focus:ring-violet-500 rounded"
      >
        {item.skipped7d}
      </span>
      <span
        role="tooltip"
        className="hidden group-hover:block group-focus-within:block absolute z-10 right-0 top-full mt-1 w-44 p-2 rounded-lg bg-zinc-800 border border-zinc-700 text-xs text-zinc-300 shadow-xl text-left"
      >
        {SKIP_REASON_LABELS.map(([key, label]) => (
          <span key={key} className="flex justify-between gap-2">
            <span>{label}</span>
            <span>{item.skippedByReason7d[key]}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

function ProducerRow({
  item,
  pending,
  error,
  onToggle,
}: {
  item: ProducerListItem;
  pending: boolean;
  error: string | null;
  onToggle: () => void;
}) {
  const killSwitchOnly = KILL_SWITCH_ONLY.has(item.producer);
  const neverRan = item.status === 'never-run';

  return (
    <tr className="border-b border-zinc-800 align-top" data-testid={`producer-row-${item.producer}`}>
      <th scope="row" className="px-4 py-3 text-left font-normal">
        <Link
          to={`/feed/producers/${encodeURIComponent(item.producer)}`}
          className="text-sm font-medium text-zinc-100 hover:text-violet-400 underline-offset-2 hover:underline"
        >
          {item.producer}
        </Link>
        <p className="text-xs text-zinc-500 mt-0.5 max-w-xs">
          {DESCRIPTIONS[item.producer] ?? FALLBACK_DESCRIPTION}
        </p>
        {neverRan && <p className="text-xs text-zinc-400 mt-1 max-w-xs">{NEVER_RUN_COPY}</p>}
        {error && (
          <p role="alert" className="text-xs text-red-400 mt-1 max-w-xs">
            {error}
          </p>
        )}
      </th>
      <td className="px-4 py-3">
        <StatusPill status={item.status} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <KillSwitch
            producer={item.producer}
            enabled={item.enabled}
            pending={pending}
            onToggle={onToggle}
          />
          {item.isDefault && <DefaultBadge />}
        </div>
      </td>
      {killSwitchOnly ? (
        <td className="px-4 py-3 text-xs text-zinc-600" colSpan={3}>
          Kill switch only
        </td>
      ) : (
        <>
          <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">
            {neverRan ? '—' : relativeTime(item.lastRunAt)}
          </td>
          <td className="px-4 py-3 text-sm text-zinc-200 text-right">
            {neverRan ? '—' : <span>Published {item.published7d}</span>}
          </td>
          <td className="px-4 py-3 text-sm text-zinc-200 text-right">
            {neverRan ? '—' : <SkipBreakdown item={item} />}
          </td>
        </>
      )}
    </tr>
  );
}

function StopDialog({
  producer,
  onConfirm,
  onCancel,
}: {
  producer: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="stop-producer-text"
        className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
      >
        <p id="stop-producer-text" className="text-sm text-zinc-100">
          {`Stop ${producer}? The next run or message writes nothing. Entries already published stay.`}
        </p>
        <div className="flex justify-end gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            aria-label={`Stop ${producer}`}
            className="min-h-[48px] px-5 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm font-medium"
          >
            Stop
          </button>
        </div>
      </div>
    </div>
  );
}

function GlobalCap({
  cap,
  isDefault,
  onSaved,
}: {
  cap: number;
  isDefault: boolean;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  function startEdit() {
    setDraft(String(cap));
    setFieldError(null);
    setEditing(true);
  }

  async function save() {
    const value = Number(draft);
    if (draft.trim() === '' || !Number.isInteger(value)) {
      setFieldError('Enter a whole number.');
      return;
    }
    setSaving(true);
    setFieldError(null);
    try {
      await setGlobalDailyCap(value);
      await onSaved();
      setEditing(false);
    } catch (err) {
      const serverMessage =
        err instanceof ProducersHttpError ? err.fieldErrors['globalDailyCapPerUser']?.[0] : undefined;
      setFieldError(serverMessage ?? errorMessage(err, 'Could not save the daily cap.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-3 mb-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
      <div>
        <p className="text-xs text-zinc-400">Global daily cap per user</p>
        {editing ? (
          <div className="flex flex-col gap-1 mt-1">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
                aria-label="Global daily cap per user"
                aria-invalid={fieldError !== null}
                aria-describedby={fieldError ? 'cap-error' : undefined}
                className="w-24 rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-violet-500"
              />
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="min-h-[48px] px-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-zinc-950 text-sm"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={saving}
                className="min-h-[48px] px-3 rounded-lg border border-zinc-600 text-zinc-300 text-sm hover:bg-zinc-800 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
            {fieldError && (
              <p id="cap-error" role="alert" className="text-xs text-red-400">
                {fieldError}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg font-semibold text-zinc-100">{cap}</span>
            {isDefault && <DefaultBadge />}
            <button
              type="button"
              onClick={startEdit}
              className="min-h-[48px] px-3 rounded-lg border border-zinc-600 text-zinc-300 text-xs hover:bg-zinc-800"
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProducersListPage() {
  const [data, setData] = useState<ProducerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingProducer, setPendingProducer] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await getProducers());
    } catch (err) {
      setLoadError(errorMessage(err, 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => { void load(); });
  }, [load]);

  // Re-fetches after a save so the row renders the server's value. A failed
  // re-fetch is surfaced by the caller, and the previous list stays on screen.
  const refresh = useCallback(async () => {
    setData(await getProducers());
  }, []);

  async function applyEnabled(producer: string, enabled: boolean) {
    setPendingProducer(producer);
    setRowErrors((e) => Object.fromEntries(Object.entries(e).filter(([name]) => name !== producer)));
    try {
      await setProducerEnabled(producer, enabled);
      await refresh();
    } catch (err) {
      setRowErrors((e) => ({ ...e, [producer]: errorMessage(err, 'Could not save the change.') }));
    } finally {
      setPendingProducer(null);
    }
  }

  function handleToggle(item: ProducerListItem) {
    if (item.enabled) {
      setConfirming(item.producer);
    } else {
      void applyEnabled(item.producer, true);
    }
  }

  function handleConfirmStop() {
    const producer = confirming;
    setConfirming(null);
    if (producer) void applyEnabled(producer, false);
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      <div className="mb-4">
        <h1 className="text-sm font-semibold text-zinc-100">Feed producers</h1>
        <p className="text-xs text-zinc-500 mt-0.5">
          System-written feed entries. Turn a producer off to stop it writing anything new.
        </p>
      </div>

      {loading && !data && (
        <div role="status" aria-label="Loading producers" className="flex flex-col gap-2">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-14 rounded-lg bg-zinc-900 border border-zinc-800 motion-safe:animate-pulse" />
          ))}
        </div>
      )}

      {loadError && !data && (
        <div role="alert" className="flex flex-col gap-2 items-start p-4 bg-zinc-900 border border-red-900/40 rounded-xl">
          <div className="flex items-center gap-1.5 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            <span>Could not load producers</span>
          </div>
          <p className="text-xs text-zinc-500 break-words">{loadError}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 min-h-[48px] px-3 rounded-lg bg-zinc-800 text-zinc-300 text-sm hover:bg-zinc-700"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {data && (
        <>
          <GlobalCap
            cap={data.globalDailyCapPerUser}
            isDefault={data.capIsDefault}
            onSaved={refresh}
          />
          <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-xl">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500">
                  <th scope="col" className="px-4 py-2 font-semibold">Producer</th>
                  <th scope="col" className="px-4 py-2 font-semibold">Status</th>
                  <th scope="col" className="px-4 py-2 font-semibold">Kill switch</th>
                  <th scope="col" className="px-4 py-2 font-semibold">Last run</th>
                  <th scope="col" className="px-4 py-2 font-semibold text-right">Published (7d)</th>
                  <th scope="col" className="px-4 py-2 font-semibold text-right">Skipped (7d)</th>
                </tr>
              </thead>
              <tbody>
                {data.producers.map((item) => (
                  <ProducerRow
                    key={item.producer}
                    item={item}
                    pending={pendingProducer === item.producer}
                    error={rowErrors[item.producer] ?? null}
                    onToggle={() => handleToggle(item)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {confirming && (
        <StopDialog
          producer={confirming}
          onConfirm={handleConfirmStop}
          onCancel={() => setConfirming(null)}
        />
      )}
    </main>
  );
}

export default ProducersListPage;
